import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ============================================================================
// Phase 19 — JWT bearer token persistence + rolling refresh
// ----------------------------------------------------------------------------
// Token issued by /users/{id}/verify-pin and /users/{id}/set-pin lives in
// localStorage under `stakes_auth_token`. The axios request interceptor
// auto-attaches it as `Authorization: Bearer <token>`; the response
// interceptor reads `X-Refreshed-Token` (returned on every authenticated
// 200) and swaps it in localStorage so the 30-day rolling expiry stays
// fresh as long as the user opens the app at least once per month.
// ============================================================================
const TOKEN_KEY = "stakes_auth_token";
export function getAuthToken() {
  try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
}
export function setAuthToken(token) {
  try { if (token) localStorage.setItem(TOKEN_KEY, token); } catch { /* ignore */ }
}
export function clearAuthToken() {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
}

const client = axios.create({ baseURL: API });

client.interceptors.request.use((config) => {
  const t = getAuthToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

client.interceptors.response.use(
  (res) => {
    const refreshed = res.headers?.["x-refreshed-token"] || res.headers?.["X-Refreshed-Token"];
    if (refreshed) setAuthToken(refreshed);
    return res;
  },
  (err) => {
    if (err?.response?.status === 401) {
      // Session expired or tampered — wipe the dead token so the next
      // PinModal entry forces a fresh sign-in.
      clearAuthToken();
    }
    return Promise.reject(err);
  }
);

// ⚡ HARDCODED FAMILY HABIT-RULE PRESETS
const TACTICAL_TEMPLATES = {
  family1: {
    rules: [
      "Drink a full bottle or large glass of water.",
      "Read a physical book for 15 minutes.",
      "Complete one household chore.",
      "The Daily Disguise: Wear something ridiculous on your head and take a photo.",
      "The 15-Minute Masterpiece: Draw, sketch, or paint something new today.",
    ],
  },
  family2: {
    rules: [
      "15 minutes of homework, studying, or brain puzzles.",
      "Eat a piece of fresh fruit or a vegetable.",
      "Pack your bag or lay out your clothes for tomorrow.",
      "Wheels & Trails: Spend 20 minutes outside cycling, skating, or walking.",
      "The Floor is Lava: Take a photo with both feet completely off the ground.",
    ],
  },
  family3: {
    rules: [
      "Brush your teeth immediately after dinner.",
      "Clean up one specific zone in your room.",
      "Write down or say 3 things you are grateful for today.",
      "The Inventor's Workshop: Build something using LEGOs or safe household objects.",
      "Optical Illusion: Take a forced-perspective photo.",
    ],
  },
};

export const api = {
  // League
  getLeague: () => client.get("/league").then((r) => r.data),
  getLeagueById: (id) => client.get(`/leagues/${id}`).then((r) => r.data),
  getLeagueByCode: (code) => client.get(`/leagues/by-code/${code}`).then((r) => r.data),
  createLeague: (data) => client.post("/league", data).then((r) => r.data),
  updateLeague: (id, data) => client.put(`/league/${id}`, data).then((r) => r.data),
  startLeague: (id) => client.post(`/league/${id}/start`).then((r) => r.data),
  unlockLeague: (id) => client.post(`/league/${id}/unlock`).then((r) => r.data),
  resetLeague: (id) => client.delete(`/league/${id}`).then((r) => r.data),
  getLeaguePins: (id, adminPin) =>
    client.get(`/leagues/${id}/pins`, { params: { admin_pin: adminPin } }).then((r) => r.data),
  getLatestSettlement: (id) =>
    client.get(`/leagues/${id}/latest-settlement`).then((r) => r.data),
  resetAdminPin: (leagueId, email, verification_token, new_pin) =>
    client.post(`/leagues/${leagueId}/reset-admin-pin`, { email, verification_token, new_pin }).then((r) => r.data),
  setRecoveryEmail: (leagueId, email, verification_token) =>
    client.post(`/leagues/${leagueId}/set-recovery-email`, { email, verification_token }).then((r) => r.data),

  // Auth (Email OTP — Resend)
  requestOtp: (email) =>
    client.post(`/auth/request-otp`, { email }).then((r) => r.data),
  verifyOtp: (email, code) =>
    client.post(`/auth/verify-otp`, { email, code }).then((r) => r.data),

  // Self-serve PIN claim (PIN must currently be empty)
  setPlayerPin: async (userId, pin) => {
    const res = await client.post(`/users/${userId}/set-pin`, { pin });
    if (res.data?.token) setAuthToken(res.data.token);
    // Backwards compat — components expect the updated user document
    return res.data.user || res.data;
  },

  // Users
  listUsers: (leagueId) => client.get(`/users`, { params: { league_id: leagueId } }).then((r) => r.data),
  createUser: (data) => client.post(`/users`, data).then((r) => r.data),
  updateUser: (id, data) => client.put(`/users/${id}`, data).then((r) => r.data),
  deleteUser: (id) => client.delete(`/users/${id}`).then((r) => r.data),
  getUser: (id) => client.get(`/users/${id}`).then((r) => r.data),
  verifyPin: async (id, pin) => {
    const r = await client.post(`/users/${id}/verify-pin`, { pin });
    if (r.data?.token) setAuthToken(r.data.token);
    return r.data;
  },
  verifyAdminPin: async (leagueId, pin) => {
    const r = await client.post(`/leagues/${leagueId}/verify-admin-pin`, { pin });
    if (r.data?.token) setAuthToken(r.data.token);
    return r.data;
  },

  // Tasks & Templates
  getTasks: (userId) => client.get(`/users/${userId}/tasks`).then((r) => r.data),
  updateTasks: (userId, rules) => client.put(`/users/${userId}/tasks`, { rules }).then((r) => r.data),
  // ⚡ NEW: Intercepts the template fetch and returns our hardcoded high-stakes copy
  getTemplate: async (key) => {
    return new Promise((resolve, reject) => {
      const template = TACTICAL_TEMPLATES[key];
      if (template) {
        resolve(template);
      } else {
        reject({ response: { data: { detail: "Template not found" } } });
      }
    });
  },

  // Check-ins
  listCheckins: (userId, date) => client.get(`/users/${userId}/checkins`, { params: { date } }).then((r) => r.data),
  upsertCheckin: (userId, date, rule_index, value) =>
    client.post(`/users/${userId}/checkins`, { date, rule_index, value }).then((r) => r.data),
  clearCheckin: (userId, date, rule_index) =>
    client.delete(`/users/${userId}/checkins`, { params: { date, rule_index } }).then((r) => r.data),
  getUserDay: (userId, date) =>
    client.get(`/users/${userId}/day`, { params: { date } }).then((r) => r.data),

  // Leaderboard & feed & activity
  getLeaderboard: (leagueId, period = "all") =>
    client.get(`/leaderboard`, { params: { league_id: leagueId, period } }).then((r) => r.data),
  getFeed: (leagueId, limit = 50) =>
    client.get(`/feed`, { params: { league_id: leagueId, limit } }).then((r) => r.data),
  getActivity: (leagueId, limit = 30) =>
    client.get(`/leagues/${leagueId}/activity`, { params: { limit } }).then((r) => r.data),
  getPastWinners: (leagueId, limit = 24) =>
    client.get(`/leagues/${leagueId}/past-winners`, { params: { limit } }).then((r) => r.data),
  // ---------- Phase 20.5 — Rogue Directive ----------
  getActiveRogue: (leagueId) =>
    client.get(`/leagues/${leagueId}/rogue/active`).then((r) => r.data),
  verifyRogue: (directiveId) =>
    client.post(`/rogue/${directiveId}/verify`).then((r) => r.data),
  rejectRogue: (directiveId) =>
    client.post(`/rogue/${directiveId}/reject`).then((r) => r.data),
  adminTriggerRogue: (leagueId) =>
    client.post(`/admin/trigger-rogue`, null, { params: { league_id: leagueId } }).then((r) => r.data),
  getRogueStatus: (leagueId) =>
    client.get(`/leagues/${leagueId}/rogue/status`).then((r) => r.data),
  // ---------- Phase 20.13 — Brag Board ----------
  getBragBoards: (leagueId, limit = 12) =>
    client.get(`/leagues/${leagueId}/brag-boards`, { params: { limit } }).then((r) => r.data),
  // ---------- Phase 20.16 — 7-Day Weekly Heatmap ----------
  getUserWeek: (userId, start) =>
    client
      .get(`/users/${userId}/week`, { params: start ? { start } : {} })
      .then((r) => r.data),
  adminTriggerBragBoard: (leagueId) =>
    client.post(`/admin/trigger-brag-board`, null, { params: { league_id: leagueId } }).then((r) => r.data),
  // ---------- Phase 20.15 — Admin Purge Utility ----------
  adminPurgeCheckins: (leagueId, before, dryRun = true) =>
    client
      .post(`/admin/purge-checkins`, null, {
        params: { league_id: leagueId, before, dry_run: dryRun },
      })
      .then((r) => r.data),
  fileUrl: (path) => `${API}/files/${path}`,

  // Challenge & Redemption
  getChallengeInfo: (date) => client.get(`/challenge-info`, { params: date ? { date } : {} }).then((r) => r.data),

  // Notifications (Web Push - Item 5.1)
  getVapidKey: () => client.get("/notifications/vapid-public-key").then((r) => r.data),
  subscribePush: (userId, subscription) =>
    client.post("/notifications/subscribe", { user_id: userId, subscription }).then((r) => r.data),

  getRedemption: (userId, leagueId) =>
    client.get(`/users/${userId}/redemption`, { params: { league_id: leagueId } }).then((r) => r.data),
  setRedemption: (userId, leagueId, task, actorId) =>
    client.post(`/users/${userId}/redemption`, { task }, { params: { league_id: leagueId, actor_id: actorId } }).then((r) => r.data),
  completeRedemption: (userId, leagueId) =>
    client.post(`/users/${userId}/redemption/complete`, null, { params: { league_id: leagueId } }).then((r) => r.data),
  suggestTask: () => client.get(`/suggest-task`).then((r) => r.data),

  // Photo upload
  uploadCheckinPhoto: (userId, date, rule_index, file) => {
    const fd = new FormData();
    fd.append("date", date);
    fd.append("rule_index", String(rule_index));
    fd.append("file", file);
    return client.post(`/users/${userId}/checkins/photo`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
};

export const ACCOUNT_COLORS = {
  admin: "#FFD60A",
  member: "#00E5FF",
  child: "#FF1493",
};

export const ACCOUNT_LABEL = {
  admin: "ADMIN",
  member: "MEMBER",
  child: "CHILD",
};

export const NEON = {
  blue: "#00E5FF",
  pink: "#FF1493",
  yellow: "#FFD60A",
  green: "#32D74B",
  red: "#FF3B30",
};